import React, { useEffect, useState, useRef } from 'react';
import { GlobeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCesium } from '../../context/CesiumContext';
import {
	NearFarScalar,
	Math as CesiumMath,
	Cesium3DTileStyle,
	Cesium3DTileset,
	Primitive,
	GeometryInstance,
	EllipsoidGeometry,
	Cartesian3,
	PerInstanceColorAppearance,
	Matrix4,
	ColorGeometryInstanceAttribute,
	Color,
} from 'cesium';
import { setTranslucentDepthPicking } from '@/helper/translucentDepth';
import { useAssetSettings } from '@/hooks/use-asset-settings';
import { useParams } from 'react-router';

interface UndergroundControlsProps {
	currentBaseMapId: string;
}

const UndergroundControls: React.FC<UndergroundControlsProps> = ({ currentBaseMapId }) => {
	const { t } = useTranslation();
	const { viewer, undergroundEnabled, setUndergroundEnabled, homeView } = useCesium();
	const { assetId } = useParams();
	const { updateSetting, getSettings } = useAssetSettings(assetId);

	// Ref to track the sphere primitive so we can remove it later
	const innerSphereRef = useRef<Primitive | null>(null);

	const [alpha, setAlpha] = useState(() => {
		const saved = getSettings();
		return typeof saved.alpha === 'number' ? saved.alpha : 0.5;
	});

	// Logic for the Black Inner Sphere
	useEffect(() => {
		if (!viewer || viewer.isDestroyed()) return;

		const scene = viewer.scene;

		// Check if active map is a Google one
		const isGoogleMap = currentBaseMapId.includes('google');

		// Must be underground AND using Google Maps
		if (undergroundEnabled && isGoogleMap) {
			if (!innerSphereRef.current) {
				const earthRadius = 6378137.0;
				// 25km smaller radius
				const innerRadius = earthRadius - 25000.0;

				const innerSphere = new Primitive({
					geometryInstances: new GeometryInstance({
						geometry: new EllipsoidGeometry({
							radii: new Cartesian3(innerRadius, innerRadius, innerRadius),
							vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT,
						}),
						modelMatrix: Matrix4.IDENTITY,
						attributes: {
							color: ColorGeometryInstanceAttribute.fromColor(Color.BLACK.withAlpha(1)),
						},
					}),
					appearance: new PerInstanceColorAppearance({
						closed: true,
						translucent: false,
					}),
					asynchronous: false,
				});

				scene.primitives.add(innerSphere);
				innerSphereRef.current = innerSphere;
				console.log('Added Inner Sphere for Google Underground mode');
			}
		} else {
			// If condition fails but we have the sphere, remove it
			if (innerSphereRef.current) {
				scene.primitives.remove(innerSphereRef.current);
				innerSphereRef.current = null;
				console.log('Removed Inner Sphere');
			}
		}

		// Cleanup on unmount
		return () => {
			if (innerSphereRef.current && !viewer.isDestroyed()) {
				viewer.scene.primitives.remove(innerSphereRef.current);
				innerSphereRef.current = null;
			}
		};
	}, [viewer, undergroundEnabled, currentBaseMapId]);

	const handleToggle = (enabled: boolean) => {
		setUndergroundEnabled(enabled);
		updateSetting('undergroundEnabled', enabled);

		if (!enabled && viewer) {
			if (homeView) {
				if (homeView.type === 'fly') {
					viewer.camera.flyTo({
						...homeView.options,
						duration: 1.5,
					});
				} else if (homeView.type === 'zoom') {
					viewer.zoomTo(homeView.target, homeView.offset).catch(console.warn);
				}
			} else {
				viewer.camera.flyHome(1.5);
			}
		}
	};

	//Handle Slider Change
	const handleAlphaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newVal = Number(e.target.value);
		setAlpha(newVal);
		updateSetting('alpha', newVal);
	};

	useEffect(() => {
		if (!viewer) return;
		const scene = viewer.scene;
		const globe = scene.globe;

		// Collision logic
		scene.screenSpaceCameraController.enableCollisionDetection = !undergroundEnabled;

		// Picking through the translucent globe onto Google 3D tiles needs translucent depth
		const googleUnderground = undergroundEnabled && currentBaseMapId.includes('google');
		scene.globe.depthTestAgainstTerrain = googleUnderground;
		setTranslucentDepthPicking(scene, 'underground', googleUnderground);

		globe.translucency.enabled = undergroundEnabled;
		if (!globe.translucency.frontFaceAlphaByDistance) {
			globe.translucency.frontFaceAlphaByDistance = new NearFarScalar(400.0, 0.0, 800.0, 1.0);
		}
	}, [viewer, undergroundEnabled, currentBaseMapId]);

	useEffect(() => {
		if (!viewer || viewer.isDestroyed()) return;

		const scene = viewer.scene;
		const globe = scene.globe;
		const clampedAlpha = CesiumMath.clamp(alpha, 0.0, 1.0);

		if (globe.translucency.frontFaceAlphaByDistance) {
			globe.translucency.frontFaceAlphaByDistance.nearValue = clampedAlpha;
			globe.translucency.frontFaceAlphaByDistance.farValue = 1;
		}

		// Handle Google 3D Tiles Opacity
		const primitives = scene.primitives;
		for (let i = 0; i < primitives.length; i++) {
			const prim = primitives.get(i);
			if (prim instanceof Cesium3DTileset && (prim as any)._isGoogleTileset) {
				if (undergroundEnabled) {
					prim.style = new Cesium3DTileStyle({
						color: `color('white', ${alpha})`,
					});
				} else {
					prim.style = new Cesium3DTileStyle({
						color: "color('white', 1.0)",
					});
				}
			}
		}
		scene.requestRender();
	}, [viewer, undergroundEnabled, alpha]);

	return (
		<div className="absolute bottom-12 left-20 z-[50] bg-card/95 backdrop-blur-md rounded-xl shadow-lg p-4 w-64 transition-all duration-200 ease-in-out">
			<div className="flex flex-col gap-3">
				<label className="flex items-center justify-between cursor-pointer group select-none">
					<div className="flex items-center gap-2 text-foreground">
						<GlobeIcon size={18} className="text-foreground" />
						<span className="text-sm font-semibold">{t('viewer.underground.title')}</span>
					</div>

					<input
						type="checkbox"
						className="sr-only"
						checked={undergroundEnabled}
						onChange={(e) => handleToggle(e.target.checked)}
					/>

					<div
						className={`w-9 h-5 rounded-full transition-colors duration-200 ease-in-out ${
							undergroundEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
						}`}
					>
						<div
							className={`h-4 w-4 bg-background rounded-full shadow transform transition-transform mt-0.5 ml-0.5 ${
								undergroundEnabled ? 'translate-x-4' : ''
							}`}
						/>
					</div>
				</label>

				{!undergroundEnabled ? (
					<div className="mt-1">
						<p className="text-xs text-muted-foreground italic">{t('viewer.underground.enableHint')}</p>
					</div>
				) : (
					<div className="mt-3 pt-2 flex items-center gap-2 animate-in fade-in zoom-in-95 duration-200">
						<span className="text-xs text-muted-foreground w-14">{t('viewer.underground.opacity')}</span>

						<input
							type="range"
							min={0}
							max={1}
							step={0.01}
							value={alpha}
							onChange={handleAlphaChange}
							className="flex-1 accent-primary"
						/>

						<span className="text-xs text-foreground w-8 text-right">
							{Math.round(alpha * 100)}%
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default UndergroundControls;
